/* ==========================================================================
   Tanvika — hero slideshow

   Crossfades the plate under stationary copy, and carries the active slide's
   copy scheme up onto the section element so the ink colour follows the
   photograph (see the scheme note in tv-hero.liquid).

   Autoplay pauses on hover, on keyboard focus, and whenever the tab is in the
   background — a slideshow that keeps advancing in a hidden tab burns battery
   and means the visitor comes back to an arbitrary slide.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function Hero(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(
      root.querySelectorAll('[data-tv-hero-slide]')
    );
    if (this.slides.length < 2) return;

    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-tv-hero-dot]'));
    this.index = 0;
    this.timer = null;

    // Floored, not just parsed: a 0 or a NaN here would spin setTimeout as
    // fast as the browser allows.
    var declared = parseInt(root.dataset.interval, 10);
    this.interval = isFinite(declared) && declared >= 2000 ? declared : 15000;
    this.autoplay = root.dataset.autoplay !== 'false' && !REDUCED.matches;

    root.style.setProperty('--tv-hero-dot-ms', this.interval + 'ms');

    this.bind();
    this.start();
  }

  Hero.prototype.bind = function () {
    var self = this;

    var prev = this.root.querySelector('[data-tv-hero-prev]');
    var next = this.root.querySelector('[data-tv-hero-next]');

    if (prev) {
      prev.addEventListener('click', function () {
        self.go(self.index - 1, true);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        self.go(self.index + 1, true);
      });
    }

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        self.go(parseInt(dot.dataset.index, 10), true);
      });
    });

    // Pause while the visitor is looking at it or tabbing through it.
    ['mouseenter', 'focusin'].forEach(function (evt) {
      self.root.addEventListener(evt, function () {
        self.pause();
      });
    });
    ['mouseleave', 'focusout'].forEach(function (evt) {
      self.root.addEventListener(evt, function () {
        self.resume();
      });
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        self.pause();
      } else {
        self.resume();
      }
    });

    this.bindSwipe();
  };

  /* Touch, not Pointer Events: a horizontal swipe here must not fight the
     page's vertical scroll, and touchmove gives both axes to compare before
     deciding this is a horizontal gesture at all. */
  Hero.prototype.bindSwipe = function () {
    var self = this;
    var x0 = null;
    var y0 = null;
    var locked = false;

    this.root.addEventListener(
      'touchstart',
      function (e) {
        if (e.touches.length !== 1) return;
        x0 = e.touches[0].clientX;
        y0 = e.touches[0].clientY;
        locked = false;
      },
      { passive: true }
    );

    this.root.addEventListener(
      'touchmove',
      function (e) {
        if (x0 === null || locked) return;
        var dx = e.touches[0].clientX - x0;
        var dy = e.touches[0].clientY - y0;
        // Only treat it as a swipe once it is clearly more horizontal than
        // vertical, or a diagonal scroll flips the slide by accident.
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          locked = true;
          self.go(dx < 0 ? self.index + 1 : self.index - 1, true);
        }
      },
      { passive: true }
    );

    this.root.addEventListener('touchend', function () {
      x0 = null;
      y0 = null;
    });
  };

  Hero.prototype.go = function (next, manual) {
    var count = this.slides.length;
    var i = ((next % count) + count) % count;
    if (i === this.index) return;

    var from = this.slides[this.index];
    var to = this.slides[i];

    from.classList.remove('is-active');
    from.setAttribute('aria-hidden', 'true');
    to.classList.add('is-active');
    to.removeAttribute('aria-hidden');

    // The ink colour belongs to the plate, so it moves with it.
    this.root.dataset.schemeDesktop = to.dataset.schemeDesktop || '';
    this.root.dataset.schemeMobile = to.dataset.schemeMobile || '';

    if (this.dots.length) {
      var oldDot = this.dots[this.index];
      var newDot = this.dots[i];
      oldDot.classList.remove('is-active');
      oldDot.setAttribute('aria-selected', 'false');
      newDot.classList.add('is-active');
      newDot.setAttribute('aria-selected', 'true');
    }

    this.index = i;

    // A manual press restarts the clock. Without this the next automatic
    // change can land a fraction of a second after the press.
    if (manual) this.start();
  };

  Hero.prototype.start = function () {
    var self = this;
    this.stop();
    if (!this.autoplay) return;
    this.root.classList.remove('is-paused');
    this.restartDot();
    this.timer = setInterval(function () {
      self.go(self.index + 1);
      self.restartDot();
    }, this.interval);
  };

  /* The draining bar is a CSS animation, and re-adding the class alone does
     not replay it — the class never actually left. Forcing a reflow between
     the removal and the re-add is what restarts it. */
  Hero.prototype.restartDot = function () {
    if (!this.dots.length) return;
    var fill = this.dots[this.index].querySelector('.tv-hero__dot-fill');
    if (!fill) return;
    fill.style.animation = 'none';
    void fill.offsetWidth;
    fill.style.animation = '';
  };

  Hero.prototype.stop = function () {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  };

  Hero.prototype.pause = function () {
    if (!this.autoplay) return;
    this.stop();
    this.root.classList.add('is-paused');
  };

  Hero.prototype.resume = function () {
    if (!this.autoplay) return;
    if (this.root.contains(document.activeElement)) return;
    if (this.root.matches(':hover')) return;
    if (document.hidden) return;
    this.root.classList.remove('is-paused');
    this.start();
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-tv-hero]').forEach(function (el) {
      if (el.dataset.tvHeroReady) return;
      el.dataset.tvHeroReady = 'true';
      new Hero(el);
    });
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  }

  // The theme editor tears a section down and rebuilds it on every change.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
